'use client'

import Link from 'next/link'
import { Fragment } from 'react'

import { Image } from '@/components/Image'
import { SectionContent } from '@/components/SectionContent'
import { SectionHeader } from '@/components/SectionHeader'

export default function Page() {
  return (
    <main className="mb-12 md:mb-20">
      <SectionHeader title="Tax Return" subtitle="確定申告の税務顧問">
        <p>
          個人のお客様の多様なニーズに合わせて、確定申告をトータルでサポートいたします。
        </p>
        <p>
          特に、複雑化する暗号資産（仮想通貨）取引に対応できる専門性を強みとし、最新の知識とノウハウを駆使して、安心・確実な申告をお手伝いします。
        </p>
      </SectionHeader>
      {/* CONTENT */}
      <section className="mt-12 md:mt-20">
        <ul className="grid px-lg md:grid-cols-2 md:gap-x-12 xl:grid-cols-4">
          {[
            {
              title: '暗号資産（仮想通貨）取引の申告',
              icon: 'currency_bitcoin',
              text: [
                'ビットコインやイーサリアムなどの主要通貨はもちろん、NFTやDeFiといった新しい取引形態も増えています。こうした取引は複雑で、損益の計算方法や税務上の扱いを誤るリスクが高い分野です。',
                'さらに、税理士が窓口となり一気通貫で対応するサービスをご用意。取引履歴の整理から確定申告書の提出まで任せられるため、安心してお任せいただけます。',
              ],
              other: [
                '取引所間の売買・送金',
                'DeFi（分散型金融）取引',
                'NFTの売買・収益化',
                'マイニング・ステーキング収益',
              ],
            },
            {
              title: '株式・不動産の譲渡所得',
              icon: 'account_balance',
              text: [
                '暗号資産以外にも、株式や投資信託の売買、不動産の売却など、申告が必要となるケースにも対応します。',
                '大きな取引や一時的な収益についても、適切な申告でリスクを回避します。',
              ],
              other: [
                '株式や投資信託の売却益・配当金',
                '不動産売却に伴う譲渡所得',
                '特例や控除の適用による税負担軽減',
              ],
            },
            {
              title: '医療費控除・各種控除申告',
              icon: 'contract',
              text: [
                '確定申告では、控除を正しく適用することで税金を抑えることが可能です。',
                '「忘れていた」「適用できると思わなかった」といったケースを防ぎ、最適な申告をご提案します。',
              ],
              other: [
                '医療費控除',
                '住宅ローン控除',
                '寄附金控除（ふるさと納税など）',
                '雑損控除',
              ],
            },
            {
              title: '個人事業主・フリーランスの方へ',
              icon: 'smart_card_reader',
              text: [
                'フリーランスや個人事業主の方にとって、確定申告は毎年の大きな負担となります。',
                '当事務所では、クラウド会計ソフトを活用し、日々の記帳から申告までを一括で支援。',
                '節税のポイントも含めてご提案し、事業活動に専念できる環境を整えます。',
              ],
            },
          ].map((a) => (
            <li
              key={a.title}
              className="row-span-3 mb-8 grid grid-rows-subgrid items-start md:mb-12"
            >
              <div className="row-span-2 grid h-full grid-rows-subgrid items-start rounded-lg border px-lg py-6 text-center border-body-15 md:px-6 md:py-8">
                <div>
                  <span className="material-symbols-outlined !text-[56px] text-primary md:!text-[64px]">
                    {a.icon}
                  </span>
                  <h2 className="mt-fz-0.75 font-semibold leading-normal">
                    {a.title}
                  </h2>
                </div>
                <div className="mt-3 grid gap-y-fz-0.75 text-left text-sm leading-loose text-body-65 md:mt-4">
                  {a.text.map((b) => (
                    <p key={b} className="">
                      {b}
                    </p>
                  ))}
                </div>
              </div>
              {a.other && (
                <div className="mt-4 grid items-start gap-y-fz-0.5 px-md text-left text-xs leading-normal text-body-65 md:mt-6 md:px-3">
                  <p className="mb-fz-0.5 border-b pb-fz-0.25 font-semibold border-body-10">
                    主な申告業務
                  </p>
                  {a.other.map((b) => (
                    <p key={b} className="">
                      - {b}
                    </p>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
      <SectionContent title="安心して任せられる理由" className="mt-4 md:mt-8">
        <ul className="grid gap-x-12 gap-y-6 md:grid-cols-3">
          {[
            {
              icon: 'workspace_premium',
              title: '最新の専門知識',
              text: (
                <>
                  暗号資産・金融商品・不動産など
                  <wbr />
                  幅広い分野をカバー
                </>
              ),
            },
            {
              icon: 'calculate',
              title: '正確な計算',
              text: (
                <>
                  複雑な取引履歴も整理・分析し、
                  <wbr />
                  正しい税務処理を実現
                </>
              ),
            },
            {
              icon: 'heart_check',
              title: '一気通貫のサポート',
              text: (
                <>
                  暗号資産の所得計算から
                  <wbr />
                  申告までワンストップ対応で、
                  <wbr />
                  申告漏れ・誤りの不安を解消
                </>
              ),
            },
          ].map((a) => (
            <li key={a.title} className="flex gap-x-4 md:block">
              <span className="material-symbols-outlined !text-[48px] text-primary">
                {a.icon}
              </span>
              <div>
                <p className="mt-1.5 text-sm font-semibold md:mt-fz-1 md:text-base">
                  {a.title}
                </p>
                <p className="mt-fz-0.75 break-keep text-sm leading-little-loose text-body-65">
                  {a.text}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </SectionContent>
      <SectionContent title="まずはご相談ください" className="mt-8 md:mt-12">
        <p className="-mt-fz-1 text-sm leading-loose md:-mt-fz-0.5 md:text-base">
          確定申告は、適切な対応をすれば税負担の軽減やリスク回避につながります。
          <br />
          複雑な暗号資産取引から一般的な申告まで、私たちにぜひご相談ください。
        </p>
      </SectionContent>
    </main>
  )
}
